import { z } from 'zod';

// One recorded action on a «buzón», as the wire delivers it. `date` and `time` arrive ALREADY
// formatted and in the locker's local time — the backend owns the timezone now (there is no
// offset-bearing instant on the wire), so the panel renders both strings verbatim.
// `user_full_name` is the Actor display string (a courier/«Usuario» name, or "Sistema" for
// system events); an empty string means genuinely absent and the UI renders «—». `registry` is
// the raw event text, carried verbatim — the panel owns the `STEPS: "…"` sentence split. `id`
// is validated as part of the contract but not surfaced: the table renders positionally.
const LockerLogEntrySchema = z.object({
  id: z.number(),
  user_full_name: z.string(),
  date: z.string(),
  time: z.string(),
  registry: z.string(),
});

// The endpoint groups the log by box↔lock pairing period: each element carries the pairing
// metadata (`lock_id`, `is_paired`, `occurred_on`) plus the entries recorded under it. The panel
// renders ONE flat table (product decision 2026-07-27), so the metadata is validated — a
// malformed group fails the parse closed — but never surfaced, keeping the domain object to
// exactly what the table renders (data minimization). `occurred_on` is nullable: a «buzón» with a
// lock but no recorded history comes back as a single period with `occurred_on: null` (a box with
// neither lock nor history is an empty array instead) — Equipo P, 2026-07-28.
const LockerLogPairingSchema = z.object({
  box_id: z.string(),
  lock_id: z.string(),
  is_paired: z.boolean(),
  occurred_on: z.string().nullable(),
  logs: z.array(LockerLogEntrySchema),
});

// Ordering is the BACKEND's: groups and entries are flattened and painted exactly as delivered
// (there is no wire instant left to sort by), so «newest-first» is a backend guarantee.
export const LockerLogSchema = z.array(LockerLogPairingSchema).transform((pairings) => ({
  events: pairings.flatMap((pairing) =>
    pairing.logs.map((entry) => ({
      actor: entry.user_full_name,
      date: entry.date,
      time: entry.time,
      registry: entry.registry,
    })),
  ),
}));

export type LockerLog = z.infer<typeof LockerLogSchema>;
export type LockerLogEvent = LockerLog['events'][number];
